import cx from 'clsx';
import { stringToInputDate, stringToInputDatetime } from 'common/helpers.ts';
import { debounce } from 'es-toolkit';
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { IconType } from 'react-icons';
import { IClose, IEye, IEyeOff } from 'ui/component/Icons.tsx';

type Props = {
  children?: React.ReactNode;
  className?: string;
  name: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  description?: React.ReactNode;
  clearIcon?: boolean;
  RightIcon?: IconType;
  registerOptions?: RegisterOptions;
  handleErrorMessages?: boolean;
  debouncedAutoSubmitForm?: string;
  debouncedAutoSubmitTimeout?: number;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Input: React.FC<Props> = ({
  children,
  className,
  name,
  label,
  description,
  clearIcon = false,
  RightIcon,
  registerOptions = {},
  handleErrorMessages = true,
  debouncedAutoSubmitForm,
  debouncedAutoSubmitTimeout = 500,
  ...otherProps
}) => {
  const { register, formState, setValue } = useFormContext();
  const [type, setType] = React.useState(otherProps.type);

  const EyeIcon = React.useMemo(() => (type === 'password' ? IEye : IEyeOff), [type]);

  React.useEffect(() => {
    setType(otherProps.type);
  }, [otherProps.type]);

  const debouncedOnChange = React.useMemo(() => {
    if (!debouncedAutoSubmitForm || !debouncedAutoSubmitTimeout) return;
    return debounce(() => {
      const form = document.querySelector(debouncedAutoSubmitForm) as HTMLFormElement;
      if (!form) return console.warn(`Input: form with selector ${debouncedAutoSubmitForm} could not be found.`);
      form.requestSubmit();
    }, debouncedAutoSubmitTimeout);
  }, [debouncedAutoSubmitForm, debouncedAutoSubmitTimeout]);

  const errors = formState.errors[name];
  const errorContent = errors?.types ? Object.values(errors.types).map((text, index) => <p key={index}>{text}</p>) : null;

  const isDatetime = React.useMemo(() => ['datetime-local', 'datetime'].includes(otherProps.type || ''), [otherProps.type]);

  const onClear = () => setValue(name, '');

  const onToggleShowPassword = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setType((prevType) => (prevType === 'password' ? 'text' : 'password'));
  };

  if (otherProps.type === 'number') registerOptions.valueAsNumber = true;
  if (otherProps.type === 'date') registerOptions.setValueAs = stringToInputDate;
  if (otherProps.type === 'datetime') registerOptions.setValueAs = stringToInputDatetime;

  return (
    <div className={cx('relative flex min-w-max flex-col justify-center', className)} data-testid="control-input">
      {label && (
        <label className="control-label" htmlFor={`input-${name}`}>
          {label}
        </label>
      )}
      <input
        id={`input-${name}`}
        className={cx(
          'relative h-[40px] w-max min-w-full border border-grey-border py-2 pl-2 text-black focus:border-blue disabled:cursor-not-allowed',
          RightIcon ? 'pr-[55px]' : 'px-3',
          errorContent && 'border-red-dark',
          otherProps.disabled && 'bg-gray-100',
          isDatetime && 'invalid:text-grey-light', // this trick is to style the datetime-local placeholder
        )}
        data-testid="input"
        {...register(name, { onChange: debouncedOnChange, disabled: !!otherProps.disabled, ...registerOptions })}
        {...otherProps}
        type={type}
      />

      {(RightIcon || clearIcon || otherProps.type === 'password') && (
        <div
          className={cx(
            'absolute flex size-8 -translate-y-1/2 flex-row fill-black hover:cursor-pointer hover:fill-gray-400',
            label ? 'top-[44px]' : 'top-[24px]',
            isDatetime ? 'right-[24px]' : 'right-[10px]',
            otherProps.type === 'password' && 'top-[48px]',
          )}
        >
          {RightIcon && <RightIcon size={24} />}
          {clearIcon && <IClose className={cx(otherProps.type === 'datetime-local' && 'right-[16px]')} size={24} onClick={onClear} />}
          {otherProps.type === 'password' && !RightIcon && <EyeIcon onClick={onToggleShowPassword} size={24} />}
        </div>
      )}

      {description && <span className="mt-1 text-grey">{description}</span>}
      {handleErrorMessages && errorContent && (
        <div data-testid="control-errors" className="red flex flex-col gap-1 py-1">
          {errorContent}
        </div>
      )}

      {children && <div className="red mt-1">{children}</div>}
    </div>
  );
};
